import type { ArcGisLayerSchema } from './arcgisLayerSchema';

/**
 * Non-ArcGIS data feeds (Open Weather Map for now). A feed is addressed by a
 * URI stored in `serviceUrl`, e.g.
 * `feed://openweathermap/daily-16d?units=metric`. COMS owns the catalog (`GET
 * /dice/external_feeds`); `STATIC_EXTERNAL_FEEDS` mirrors it so pickers render
 * before that request returns.
 */

export const FEED_URI_PREFIX = 'feed://';
export const OWM_PROVIDER = 'openweathermap';
export const OWM_GROUP_LABEL = 'Open Weather Map (global)';
export const OWM_SOURCE_NAME = 'Open Weather Map';
/**
 * `external_layers.item_type` for Hub layers that read an Open Weather Map
 * feed.
 */
export const OWM_ITEM_TYPE = 'OpenWeatherMap';
/** Fastest check frequency for rules that read a feed. */
export const FEED_MIN_CHECK_FREQUENCY = '0 * * * *';
export const FEED_FREQUENCY_NOTE =
  'Open Weather Map feeds refresh at most hourly.';

export type FeedUnits = 'metric' | 'imperial';
export const FEED_UNITS: FeedUnits[] = ['metric', 'imperial'];
export const DEFAULT_FEED_UNITS: FeedUnits = 'metric';
export const FEED_UNIT_OPTIONS: { value: FeedUnits; label: string }[] = [
  { value: 'metric', label: 'Metric (km/h, mm, °C)' },
  { value: 'imperial', label: 'Imperial (mph, in, °F)' },
];

export type FeedQuantity = 'speed' | 'precip' | 'temp';

export type ExternalFeedField = {
  name: string;
  alias: string;
  type: 'esriFieldTypeDouble' | 'esriFieldTypeString' | 'esriFieldTypeDate';
  quantity?: FeedQuantity;
  unit?: string;
};

export type ExternalFeedDef = {
  provider: string;
  product: string;
  label: string;
  displayName: string;
  description: string;
  minCheckFrequency: string;
  primaryField: string;
  fields: Record<FeedUnits, ExternalFeedField[]>;
};

export type ParsedFeedUri = {
  provider: string;
  product: string;
  units: FeedUnits;
};

export function isFeedUri(url: string | null | undefined): boolean {
  return typeof url === 'string' && url.trim().startsWith(FEED_URI_PREFIX);
}

export function parseFeedUri(
  uri: string | null | undefined
): ParsedFeedUri | null {
  if (!isFeedUri(uri)) return null;
  const rest = uri!.trim().slice(FEED_URI_PREFIX.length);
  const [path, query = ''] = rest.split('?');
  const [provider, product] = path.split('/').filter(Boolean);
  if (!provider || !product) return null;
  const units = new URLSearchParams(query).get('units');
  return {
    provider: provider.toLowerCase(),
    product: product.toLowerCase(),
    units: units === 'imperial' ? 'imperial' : DEFAULT_FEED_UNITS,
  };
}

export function buildFeedUri(parsed: ParsedFeedUri): string {
  return `${FEED_URI_PREFIX}${parsed.provider}/${parsed.product}?units=${parsed.units}`;
}

export function withFeedUnits(uri: string, units: FeedUnits): string {
  const parsed = parseFeedUri(uri);
  return parsed ? buildFeedUri({ ...parsed, units }) : uri;
}

export function isOwmFeedUri(url: string | null | undefined): boolean {
  return parseFeedUri(url)?.provider === OWM_PROVIDER;
}

const UNIT_LABELS: Record<FeedQuantity, Record<FeedUnits, string>> = {
  speed: { metric: 'km/h', imperial: 'mph' },
  precip: { metric: 'mm', imperial: 'in' },
  temp: { metric: '°C', imperial: '°F' },
};

export function unitLabel(quantity: FeedQuantity, units: FeedUnits): string {
  return UNIT_LABELS[quantity][units];
}

function toMetric(q: FeedQuantity, value: number, from: FeedUnits): number {
  if (from === 'metric') return value;
  if (q === 'speed') return value * 1.609344;
  if (q === 'precip') return value * 25.4;
  return ((value - 32) * 5) / 9;
}

function fromMetric(q: FeedQuantity, value: number, to: FeedUnits): number {
  if (to === 'metric') return value;
  if (q === 'speed') return value / 1.609344;
  if (q === 'precip') return value / 25.4;
  return (value * 9) / 5 + 32;
}

/**
 * Converts a threshold between unit systems, rounded the way COMS rounds feed
 * values.
 */
export function convertFeedValue(
  q: FeedQuantity,
  value: number,
  from: FeedUnits,
  to: FeedUnits
): number {
  if (from === to) return value;
  const converted = fromMetric(q, toMetric(q, value, from), to);
  const decimals = q === 'precip' && to === 'imperial' ? 2 : 1;
  const factor = 10 ** decimals;
  return Math.round(converted * factor) / factor;
}

type FieldSpec = {
  name: string;
  alias: string;
  quantity?: FeedQuantity;
  type?: ExternalFeedField['type'];
};

const HOURLY_FIELDS: FieldSpec[] = [
  { name: 'max_gust_24h', alias: 'Max gust, next 24 h', quantity: 'speed' },
  { name: 'max_gust_48h', alias: 'Max gust, next 48 h', quantity: 'speed' },
  { name: 'max_gust_96h', alias: 'Max gust, next 96 h', quantity: 'speed' },
  { name: 'peak_gust_at', alias: 'Peak gust time', type: 'esriFieldTypeDate' },
  {
    name: 'max_wind_24h',
    alias: 'Max sustained wind, next 24 h',
    quantity: 'speed',
  },
  {
    name: 'max_wind_48h',
    alias: 'Max sustained wind, next 48 h',
    quantity: 'speed',
  },
  { name: 'rain_24h', alias: 'Rain, next 24 h', quantity: 'precip' },
  { name: 'rain_72h', alias: 'Rain, next 72 h', quantity: 'precip' },
  {
    name: 'max_temp_24h',
    alias: 'Max temperature, next 24 h',
    quantity: 'temp',
  },
  {
    name: 'min_temp_24h',
    alias: 'Min temperature, next 24 h',
    quantity: 'temp',
  },
  {
    name: 'worst_condition',
    alias: 'Worst condition',
    type: 'esriFieldTypeString',
  },
  { name: 'worst_condition_id', alias: 'Worst condition code' },
];

const DAILY_FIELDS: FieldSpec[] = [
  {
    name: 'max_wind_7d',
    alias: 'Max daily wind, next 7 days',
    quantity: 'speed',
  },
  {
    name: 'max_wind_16d',
    alias: 'Max daily wind, next 16 days',
    quantity: 'speed',
  },
  { name: 'max_gust_16d', alias: 'Max gust, next 16 days', quantity: 'speed' },
  { name: 'rain_7d', alias: 'Rain total, next 7 days', quantity: 'precip' },
  { name: 'rain_16d', alias: 'Rain total, next 16 days', quantity: 'precip' },
  {
    name: 'max_temp_16d',
    alias: 'Max temperature, next 16 days',
    quantity: 'temp',
  },
  {
    name: 'min_temp_16d',
    alias: 'Min temperature, next 16 days',
    quantity: 'temp',
  },
  {
    name: 'peak_wind_day',
    alias: 'Peak wind day',
    type: 'esriFieldTypeString',
  },
];

const COMMON_FIELDS: FieldSpec[] = [
  { name: 'asset_name', alias: 'Asset', type: 'esriFieldTypeString' },
  { name: 'fetched_at', alias: 'Forecast fetched', type: 'esriFieldTypeDate' },
  { name: 'units', alias: 'Units', type: 'esriFieldTypeString' },
];

function fieldsFor(specs: FieldSpec[], units: FeedUnits): ExternalFeedField[] {
  return [...specs, ...COMMON_FIELDS].map((spec) => {
    const unit = spec.quantity ? unitLabel(spec.quantity, units) : undefined;
    return {
      name: spec.name,
      alias: unit ? `${spec.alias} (${unit})` : spec.alias,
      type: spec.type ?? 'esriFieldTypeDouble',
      ...(spec.quantity ? { quantity: spec.quantity, unit } : {}),
    };
  });
}

function owmFeed(
  product: string,
  label: string,
  description: string,
  primaryField: string,
  specs: FieldSpec[]
): ExternalFeedDef {
  return {
    provider: OWM_PROVIDER,
    product,
    label,
    displayName: `${OWM_SOURCE_NAME} · ${label}`,
    description,
    minCheckFrequency: FEED_MIN_CHECK_FREQUENCY,
    primaryField,
    fields: {
      metric: fieldsFor(specs, 'metric'),
      imperial: fieldsFor(specs, 'imperial'),
    },
  };
}

export const STATIC_EXTERNAL_FEEDS: ExternalFeedDef[] = [
  owmFeed(
    'pro-hourly-4d',
    'Hourly forecast (4 days)',
    'Hourly wind speed, gusts, rain, temperature and conditions at each asset, worldwide.',
    'max_gust_96h',
    HOURLY_FIELDS
  ),
  owmFeed(
    'daily-16d',
    'Daily forecast (16 days)',
    'Daily wind, gusts, rain and temperature at each asset for the next 16 days, worldwide.',
    'max_wind_16d',
    DAILY_FIELDS
  ),
];

export function findExternalFeed(
  uri: string | null | undefined,
  catalog: ExternalFeedDef[] = STATIC_EXTERNAL_FEEDS
): ExternalFeedDef | null {
  const parsed = parseFeedUri(uri);
  if (!parsed) return null;
  return (
    catalog.find(
      (feed) =>
        feed.provider === parsed.provider && feed.product === parsed.product
    ) ?? null
  );
}

/** "Open Weather Map · Daily forecast (16 days)", or null for non-feed URLs. */
export function feedDisplayName(
  uri: string | null | undefined,
  catalog?: ExternalFeedDef[]
): string | null {
  const feed = findExternalFeed(uri, catalog);
  if (feed) return feed.displayName;
  return isOwmFeedUri(uri) ? OWM_SOURCE_NAME : null;
}

export function feedFields(
  uri: string | null | undefined,
  catalog?: ExternalFeedDef[]
): ExternalFeedField[] {
  const parsed = parseFeedUri(uri);
  const feed = findExternalFeed(uri, catalog);
  return parsed && feed ? feed.fields[parsed.units] : [];
}

/** "Updated just now", "Updated 12 min ago", "Updated 3 h ago". */
export function formatUpdatedAgo(
  fetchedAt: string | null | undefined,
  now: number = Date.now()
): string | null {
  const at = fetchedAt ? Date.parse(fetchedAt) : NaN;
  if (Number.isNaN(at)) return null;
  const minutes = Math.max(0, Math.floor((now - at) / 60_000));
  if (minutes < 1) return 'Updated just now';
  if (minutes < 60) return `Updated ${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Updated ${hours} h ago`;
  return `Updated ${Math.floor(hours / 24)} d ago`;
}

const ESRI_TO_SCHEMA_TYPE = {
  esriFieldTypeDouble: 'double',
  esriFieldTypeString: 'string',
  esriFieldTypeDate: 'date',
} as const;

/** The feed's static fields in the shape the rule editor's field picker reads. */
export function feedLayerSchema(
  uri: string | null | undefined,
  catalog?: ExternalFeedDef[]
): ArcGisLayerSchema | null {
  const feed = findExternalFeed(uri, catalog);
  if (!feed) return null;
  return {
    layerLabel: feed.displayName,
    objectIdField: 'resource_set_id',
    displayField: 'asset_name',
    fields: feedFields(uri, catalog)
      .filter((field) => field.name !== 'fetched_at' && field.name !== 'units')
      .map((field) => ({
        name: field.name,
        alias: field.alias,
        type: ESRI_TO_SCHEMA_TYPE[field.type],
      })),
    capabilities: { supportsQuery: false, supportsDistinct: false },
  };
}
